import { getFiscalIdConfig } from './fiscalIdentifier';

export interface BalanceReadStatus {
  text: string;
  icon: string;
  variant: 'success' | 'warning' | 'danger' | 'info';
  isAvailable: boolean;
}

/**
 * Mensajes informativos para el usuario sobre el estado del balance
 */
export const BALANCE_STATUS_MESSAGES = {
  NO_BALANCE_LIST: 'No hay balance cargado. Podrás crear uno manualmente en la siguiente pantalla.',
  NO_BALANCE_PAGE: 'No hay balance cargado. Podrás crear un balance manualmente en el paso "Carga de balance".',
  PROCESSING: 'El balance está en proceso. Por favor, espere a que esté disponible.',
  FAILED: 'El balance falló al procesarse. Podrás crear uno manualmente en la siguiente pantalla.',
} as const;

/**
 * Determina el estado de lectura del balance basado en processing_status
 * @param processingStatus - Estado de procesamiento del balance (MANUAL, COMPLETED, PROCESSING, FAILED, o null)
 * @returns Objeto con información del estado de lectura
 */
export const getBalanceReadStatus = (
  processingStatus: string | null | undefined
): BalanceReadStatus => {
  // Si no hay processingStatus (null o undefined), significa que no hay balance cargado
  // En este caso, permitimos acceso para que el usuario pueda crear un balance manualmente
  if (!processingStatus) {
    return {
      text: 'Sin balance',
      icon: 'help_circle_outline',
      variant: 'info',
      isAvailable: true,
    };
  }

  const status = processingStatus.toUpperCase();

  switch (status) {
    case 'MANUAL':
    case 'COMPLETED':
      return {
        text: 'Disponibles',
        icon: 'check_circle_outline',
        variant: 'success',
        isAvailable: true,
      };
    case 'PROCESSING':
      return {
        text: 'En proceso',
        icon: 'clock_outline',
        variant: 'warning',
        isAvailable: false,
      };
    case 'FAILED':
      return {
        text: 'No disponible',
        icon: 'error_circle_outline',
        variant: 'danger',
        isAvailable: true, // Permitir acceso para crear balance manual
      };
    default:
      return {
        text: 'No disponible',
        icon: 'error_circle_outline',
        variant: 'danger',
        isAvailable: true, // Permitir acceso para estados desconocidos también
      };
  }
};

/**
 * Mensajes por código de error del procesamiento del balance (i18n-ready).
 * Permite al front mostrar mensajes por idioma/tamaño.
 */
export const getBalanceProcessingErrorMessage = (
  errorCode: string | null | undefined,
  errorContext?: string | null,
  country?: string | null,
): string => {
  const fiscalId = getFiscalIdConfig(country);
  if (!errorCode) {
    return 'El balance falló al procesarse.';
  }
  const messages: Record<string, string> = {
    CUIT_MISMATCH: fiscalId.mismatchMessage,
    LLM_FORMAT_ERROR: 'No se pudo interpretar la respuesta del análisis.',
    PROCESSING_ERROR: 'Ocurrió un error al procesar el balance.',
    READING_TIMEOUT:
      'La lectura automática del balance excedió el tiempo máximo. Podrás cargarlo manualmente o devolver la carpeta a Originación.',
  };
  const message = messages[errorCode] ?? messages.PROCESSING_ERROR;
  if (errorContext && errorCode === 'CUIT_MISMATCH') {
    try {
      const ctx = JSON.parse(errorContext) as { balanceCuit?: string; folderCuit?: string };
      if (ctx.balanceCuit != null && ctx.folderCuit != null) {
        return fiscalId.mismatchWithValues(ctx.balanceCuit, ctx.folderCuit);
      }
    } catch {
      // ignore invalid JSON
    }
  }
  return message;
};

